import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API, formatApiError, mediaUrl } from "@/lib/api";
import { openRazorpayCheckout } from "@/lib/razorpay";
import PhoneVerify from "@/components/PhoneVerify";
import PaymentBadge from "@/components/PaymentBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { FileDown, MessageCircle, PackageSearch, RefreshCw, ScrollText, Search } from "lucide-react";

const STAGES = ["pending_payment", "confirmed", "processing", "shipped", "delivered"];

function Timeline({ status }) {
  const idx = STAGES.indexOf(status);
  if (idx < 0) return null;
  return (
    <div className="flex items-center gap-1 mt-4" data-testid="track-timeline">
      {STAGES.map((s, i) => (
        <div key={s} className="flex-1">
          <div className={`h-1.5 rounded-full ${i <= idx ? "bg-primary" : "bg-border"}`} />
          <div className={`text-[10px] mt-1.5 capitalize ${i <= idx ? "text-primary" : "text-muted-foreground"}`}>
            {s.replace("_", " ")}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function OrderTracking() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [token, setToken] = useState("");
  const [orders, setOrders] = useState(null);
  const [busy, setBusy] = useState(false);
  const [wa, setWa] = useState("");

  React.useEffect(() => {
    api.get("/settings").then(({ data }) => setWa(data.whatsapp_number || "")).catch(() => {});
  }, []);

  const load = async (phoneToken) => {
    setBusy(true);
    try {
      const { data } = await api.post("/orders/track", { phone, phone_token: phoneToken });
      setOrders(data);
      if (data.length === 0) toast.info("No orders found for this number yet");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onVerified = (t) => {
    setToken(t);
    load(t);
  };

  const retry = async (order) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/retry-payment`, { phone_token: token });
      await openRazorpayCheckout({
        order: data,
        prefill: { name: data.user_name, email: data.user_email, contact: data.user_phone },
        onSuccess: async (resp) => {
          try {
            await api.post("/orders/verify-payment", {
              order_id: order.id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful — invoice is on its way to your inbox");
            load(token);
          } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
          }
        },
        onFailure: (msg) => toast.error(msg),
        onDismiss: () => toast.info("Payment cancelled — you can retry any time"),
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const whatsappHelp = (order) => {
    const num = (wa || "").replace(/\D/g, "");
    if (!num) return toast.error("WhatsApp number not configured yet");
    const msg =
      `Namaste! I need help with my Immortal Vedas order.\n\n` +
      `• Order: #${order.id.slice(0, 8)}\n` +
      `• Amount: ₹${order.total?.toFixed(2)}\n` +
      `• Payment: ${order.payment_status}\n` +
      `• Items: ${order.items?.map((i) => `${i.name} x${i.quantity}`).join(", ")}\n\n` +
      `Please help me complete / check this order.`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <main className="max-w-4xl mx-auto px-6 lg:px-12 py-12">
      <div className="overline mb-2">Order Tracking</div>
      <h1 className="font-display text-4xl lg:text-5xl mb-2">Where is my order?</h1>
      <p className="text-muted-foreground mb-10 text-sm max-w-lg">
        Enter the phone number you used at checkout. We&apos;ll send a one-time code and show every order placed with it.
      </p>

      {!token && (
        <div className="border border-border rounded-2xl p-6 bg-card max-w-md space-y-4" data-testid="track-form">
          <div>
            <Label className="overline">Phone Number</Label>
            <Input
              data-testid="track-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 98xxxxxxxx"
              className="mt-2 bg-transparent"
            />
          </div>
          <PhoneVerify phone={phone} verified={false} onVerified={onVerified} />
        </div>
      )}

      {orders && orders.length === 0 && (
        <div className="text-center py-16 border border-dashed border-border rounded-2xl" data-testid="track-empty">
          <PackageSearch className="mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No orders found for {phone}.</p>
        </div>
      )}

      <div className="space-y-4">
        {(orders || []).map((o) => (
          <div key={o.id} className="border border-border rounded-2xl p-6 bg-card" data-testid={`track-order-${o.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm">#{o.id.slice(0, 8)}</span>
                  <PaymentBadge status={o.payment_status} needsReview={o.needs_review} testid={`track-pay-${o.id}`} />
                  <Badge variant="outline" className="text-[10px] capitalize">{o.status?.replace("_", " ")}</Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {new Date(o.created_at).toLocaleString()}
                  {o.invoice_number ? ` · ${o.invoice_number}` : ""}
                </div>
              </div>
              <div className="font-display text-2xl">₹{o.total?.toFixed(2)}</div>
            </div>

            <div className="flex flex-wrap gap-3 mt-4">
              {o.items?.map((it) => (
                <div key={`${o.id}-${it.product_id}`} className="flex items-center gap-2 text-sm">
                  <div className="w-10 h-10 rounded-lg bg-muted overflow-hidden shrink-0">
                    {it.image && <img src={mediaUrl(it.image)} alt={it.name} className="w-full h-full object-cover" />}
                  </div>
                  <span>{it.name} × {it.quantity}</span>
                </div>
              ))}
            </div>

            <Timeline status={o.status} />

            <div className="flex flex-wrap gap-2 mt-5">
              <Button
                variant="outline"
                className="btn-pill h-10"
                onClick={() => navigate(`/orders/${o.id}`, { state: { token } })}
                data-testid={`track-details-${o.id}`}
              >
                <ScrollText size={14} className="mr-2" /> Order Details
              </Button>
              {o.payment_status === "paid" ? (
                <Button
                  variant="outline"
                  className="btn-pill h-10 border-primary/30"
                  onClick={() => window.open(`${API}/orders/${o.id}/invoice?token=${encodeURIComponent(token)}`, "_blank")}
                  data-testid={`track-invoice-${o.id}`}
                >
                  <FileDown size={14} className="mr-2" /> Download GST Invoice
                </Button>
              ) : (
                <Button
                  onClick={() => retry(o)}
                  disabled={busy}
                  className="btn-pill h-10 bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid={`track-retry-${o.id}`}
                >
                  <RefreshCw size={14} className="mr-2" /> Retry Payment
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => whatsappHelp(o)}
                className="btn-pill h-10 border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10"
                data-testid={`track-whatsapp-${o.id}`}
              >
                <MessageCircle size={14} className="mr-2" /> Help on WhatsApp
              </Button>
            </div>
          </div>
        ))}
      </div>

      {token && (
        <Button variant="ghost" onClick={() => load(token)} disabled={busy} className="mt-6" data-testid="track-refresh">
          <Search size={14} className="mr-2" /> {busy ? "Refreshing…" : "Refresh status"}
        </Button>
      )}
    </main>
  );
}
